// Callables that are NOT in the default region (us-central1), by name. Mirrors the API's
// callable-regions.json: us-central1 Cloud Run is at its 1,000-service cap, so new callables
// deploy to us-east1 (Szymon, 2026-10-21: "keep us-east1. Make sure every client calling a
// us-east1 callable sets that region explicitly"). A name missing here is called in
// us-central1 and answers 404 from Firebase if it lives elsewhere.
//
// Add a name here in the same PR that first calls it, and keep the list in step with the API's.
// Browser calls pick the region through callableFor (composables/callable.ts); a server proxy
// that builds a cloudfunctions.net URL uses callableRegion(name). callableRegions.test.ts
// fails if a listed name is called any other way.

export type CallableRegion = 'us-central1' | 'us-east1'

export const DEFAULT_CALLABLE_REGION: CallableRegion = 'us-central1'

export const CALLABLE_REGIONS: Readonly<Record<string, CallableRegion>> = Object.freeze({
  // functions-automations
  workflowBranchingPreview: 'us-east1',
  workflowBranchingReview: 'us-east1',
  workflowBranchingRun: 'us-east1',
  workflowBranchingRuns: 'us-east1',
  workflowBranchingSave: 'us-east1',
  // functions-payments
  updateAfterPaymentSettings: 'us-east1',
  updateServiceAfterPayment: 'us-east1',
  publicInstallmentAutopayAction: 'us-east1',
  // Square pay pages (SQ-API-3, Square + memberships): the public Square charge
  squarePayToken: 'us-east1',
  // functions-payments, memberships (PORTAL-1: proposed portal-facing names, not deployed yet;
  // 369.dev calls them only with runtimeConfig.membershipDataSource = 'api')
  portalMembershipGet: 'us-east1',
  portalMembershipFreeze: 'us-east1',
  portalMembershipCancel: 'us-east1',
  portalMembershipUpdateCard: 'us-east1',
  passPublicGet: 'us-east1',
  // Wallet links (WAL-SITE-1): passGoogleSaveLink is a callable, passApple a plain https function
  passGoogleSaveLink: 'us-east1',
  passApple: 'us-east1',
  portalMembershipPayments: 'us-east1',
  portalMembershipChangePlan: 'us-east1',
  // PC-SITE-2: the membership card page (PC-API-4), relayed by server/utils/membershipCardProxy.ts
  membershipCardPublic: 'us-east1',
  portalMembershipRequest: 'us-east1',
  // functions-public (an onRequest endpoint; the /ap-event proxy builds its URL with callableRegion)
  recordAfterPaymentEvent: 'us-east1',
  // functions-booking
  getPublicBookingPaymentStatus: 'us-east1',
  checkPublicBookingSingleUseLink: 'us-east1',
  publicMeetingPoll: 'us-east1',
})

/** The region a callable is deployed in. */
export function callableRegion(name: string): CallableRegion {
  return Object.prototype.hasOwnProperty.call(CALLABLE_REGIONS, name) ? CALLABLE_REGIONS[name]! : DEFAULT_CALLABLE_REGION
}
